/**
 * Whether another tab of this browser is already on a call.
 *
 * Every tab that mounts the dashboard registers its own Telnyx session, and all
 * of them share one SIP credential — the device id behind it lives in
 * localStorage, which is per-origin, not per-tab. So Telnyx forks an inbound
 * call to every open tab and they all ring at once. Each tab's call map is its
 * own, though: the tab that is NOT handling the live call sees no calls at all,
 * concludes nothing is in progress, and plays the full ringtone straight over
 * the conversation happening in the other tab. That is the loud second ring
 * CSRs hear, and the call-waiting beep can't fix it, because the tab making the
 * noise doesn't know there is a call to be waiting behind.
 *
 * So tabs tell each other when they are busy. Presence is advertised rather
 * than asked for: a tab announces its state on every change and on a heartbeat,
 * and anything it said expires. A tab that is force-quit never gets to say
 * goodbye, and without the expiry its "busy" would latch on and silence the
 * ringtone for good — a far worse bug than the one this fixes.
 */

const CHANNEL_NAME = 'notifi-voip-call-presence';
/** Re-announce this often, so a peer that missed a message catches up. */
const HEARTBEAT_MS = 4_000;
/** Silence for longer than this and a peer is assumed gone. Comfortably more
 *  than one heartbeat, so an ordinary scheduling hiccup can't drop a live tab. */
const PRESENCE_TTL_MS = 12_000;

type PresenceMessage =
  | { type: 'state'; tabId: string; busy: boolean }
  | { type: 'hello'; tabId: string }
  | { type: 'bye'; tabId: string };

const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const peers = new Map<string, { busy: boolean; at: number }>();
const listeners = new Set<(peerBusy: boolean) => void>();

let channel: BroadcastChannel | null = null;
let heartbeat: ReturnType<typeof setInterval> | undefined;
let localBusy = false;
let lastReported = false;

/** Is any peer busy? Prunes what it walks, so a tab that stopped talking is
 *  forgotten here rather than needing a sweep of its own. */
function peerBusy(): boolean {
  const cutoff = Date.now() - PRESENCE_TTL_MS;
  let busy = false;
  for (const [id, peer] of peers) {
    if (peer.at < cutoff) {
      peers.delete(id);
      continue;
    }
    if (peer.busy) busy = true;
  }
  return busy;
}

function notify(): void {
  const busy = peerBusy();
  if (busy === lastReported) return;
  lastReported = busy;
  listeners.forEach((listener) => listener(busy));
}

function post(message: PresenceMessage): void {
  try {
    channel?.postMessage(message);
  } catch {
    // Channel closed under us (the tab is going away) — nothing to do.
  }
}

function sayBye(): void {
  post({ type: 'bye', tabId });
}

function open(): void {
  // No BroadcastChannel (older Safari, storage partitioned off) means no peer
  // presence, which is exactly how this behaved before — never worse.
  if (channel || typeof BroadcastChannel === 'undefined') return;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return;
  }

  channel.onmessage = (event: MessageEvent<PresenceMessage>) => {
    const message = event.data;
    if (!message || message.tabId === tabId) return;
    if (message.type === 'bye') {
      peers.delete(message.tabId);
      notify();
      return;
    }
    if (message.type === 'hello') {
      // A tab just opened and knows nothing yet — tell it where we stand.
      post({ type: 'state', tabId, busy: localBusy });
      return;
    }
    peers.set(message.tabId, { busy: message.busy, at: Date.now() });
    notify();
  };

  // The heartbeat re-announces AND re-evaluates: a peer that died silently is
  // only noticed by its presence going stale, which nothing else would catch.
  heartbeat = setInterval(() => {
    post({ type: 'state', tabId, busy: localBusy });
    notify();
  }, HEARTBEAT_MS);
  window.addEventListener('pagehide', sayBye);

  post({ type: 'hello', tabId });
  post({ type: 'state', tabId, busy: localBusy });
}

function close(): void {
  if (!channel) return;
  clearInterval(heartbeat);
  heartbeat = undefined;
  window.removeEventListener('pagehide', sayBye);
  sayBye();
  try {
    channel.close();
  } catch {
    // Already closed.
  }
  channel = null;
  peers.clear();
  lastReported = false;
}

/** Tell the other tabs whether this one has a call in progress. */
export function publishCallPresence(busy: boolean): void {
  localBusy = busy;
  open();
  post({ type: 'state', tabId, busy });
}

/** Watch whether any OTHER tab has a call in progress. Fires immediately with
 *  what is known, then on every change. */
export function subscribeToCallPresence(onChange: (peerBusy: boolean) => void): () => void {
  listeners.add(onChange);
  open();
  onChange(peerBusy());
  return () => {
    listeners.delete(onChange);
    if (!listeners.size) close();
  };
}

/** Test seam — drops everything this module is holding between cases. */
export function resetCallPresenceForTests(): void {
  listeners.clear();
  close();
  localBusy = false;
}
